import type { ValidData as AttachmentsFormValidData } from '@/components/forms/DetailedJobAttachmentsForm.vue';
import type { DataItem as QueryDataItem } from '@/components/tables/DetailedJobQueriesTable.vue';
import type { DataItem as TicketDataItem } from '@/components/tables/DetailedJobTicketsTable.vue';
import type { DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, toRef } from 'vue';

import { useAuthStore } from '@/stores/useAuthStore';
import { useDialogStore } from '@/stores/useDialogStore';
import { useJobEditStore } from '@/stores/useJobEditStore';
import {
  fetchGetDashboardCustomer,
  fetchGetJobTicketsSearchByJob,
  fetchGetZammadChannelsEmails,
  fetchPostJobAttachment,
} from '@/helpers/fetchers';
import * as detailedJobQueriesTableHelpers from '@/helpers/tables/detailedJobQueries';
import * as detailedJobTicketsTableHelpers from '@/helpers/tables/detailedJobTickets';

export function useJobEditOtherData() {
  const jobEditStore = useJobEditStore();
  const dialogStore = useDialogStore();
  const authStore = useAuthStore();

  const isStaff = toRef(authStore, 'isStaff');

  const ticketsData = ref<openapi.JobGetTicketsByJobShortContentOpenapi>();
  const customerOpsEmails = ref<string[]>([]);
  const channelsEmails = ref<openapi.ZammadChannelsEmailsContentOpenapi>([]);
  const isFetchingTickets = ref(false);
  const isFetchingAttachments = ref(false);

  const attachments = computed(() => jobEditStore.jobData?.attachments || []);

  const queriesTableData = computed<QueryDataItem[]>(() => {
    if (!jobEditStore.jobData?.messages.length) return [];
    return jobEditStore.jobData.messages
      .filter((message) => !message.is_private || isStaff.value)
      .map((message) => detailedJobQueriesTableHelpers.getTableItem(message));
  });

  const ticketsTableData = computed<TicketDataItem[]>(() => {
    if (!ticketsData.value?.length) return [];
    return ticketsData.value.map((ticket) =>
      detailedJobTicketsTableHelpers.getTableItem(ticket),
    );
  });

  async function fetchTickets(): Promise<void> {
    if (!jobEditStore.jobData) return;
    const jobId = jobEditStore.jobData.rockhopper_id;
    const jobDate = jobEditStore.jobData.rockhopper_date;
    if (!jobId || !jobDate) return;

    isFetchingTickets.value = true;
    try {
      ticketsData.value = await fetchGetJobTicketsSearchByJob({
        job_id: jobId,
        job_date: jobDate,
      });
    } finally {
      isFetchingTickets.value = false;
    }
  }

  async function fetchCustomerOpsEmails(): Promise<void> {
    if (!jobEditStore.jobData) return;
    const customerCode = jobEditStore.jobData.customer;
    if (!customerCode) return;

    try {
      const customer = await fetchGetDashboardCustomer({ code: customerCode });
      customerOpsEmails.value = customer.ops_emails || [];
    } catch {
      customerOpsEmails.value = [];
    }
  }

  async function fetchChannelsEmails(): Promise<void> {
    try {
      channelsEmails.value = await fetchGetZammadChannelsEmails();
    } catch {
      channelsEmails.value = [];
    }
  }

  function handleNewMessage(): void {
    if (!jobEditStore.jobData) return;

    const dialogName: DialogName = 'newQuery';
    dialogStore
      .open(dialogName, { job: jobEditStore.jobData })
      .confirmed(async function () {
        await jobEditStore.loadJob();
      });
  }

  function handleNewTicket(): void {
    if (!jobEditStore.jobData) return;

    const dialogName: DialogName = 'newTicket';
    dialogStore
      .open(dialogName, {
        jobId: jobEditStore.jobData.rockhopper_id,
        jobDate: jobEditStore.jobData.rockhopper_date,
        opsEmails: customerOpsEmails.value,
        channelsEmails: channelsEmails.value,
      })
      .confirmed(async function () {
        await jobEditStore.loadJob();
        await fetchTickets();
      });
  }

  async function handleSubmitAttachments(
    formData: AttachmentsFormValidData,
    cb: () => void,
  ): Promise<void> {
    if (!jobEditStore.jobData) {
      cb();
      return;
    }
    if (!formData.attachments?.length) {
      cb();
      return;
    }
    if (isFetchingAttachments.value) return;

    isFetchingAttachments.value = true;
    try {
      await fetchPostJobAttachment({
        rh_id: jobEditStore.jobData.rockhopper_id,
        rh_date: jobEditStore.jobData.rockhopper_date,
        files: formData.attachments,
      });
      await jobEditStore.loadJob();
    } finally {
      cb();
      isFetchingAttachments.value = false;
    }
  }

  return {
    isStaff,
    attachments,
    queriesTableData,
    ticketsTableData,
    isFetchingTickets,
    isFetchingAttachments,
    fetchTickets,
    fetchCustomerOpsEmails,
    fetchChannelsEmails,
    handleNewMessage,
    handleNewTicket,
    handleSubmitAttachments,
  };
}
